import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toaster';
import { Checkbox, Button } from '../../components/ui';
import { captureError, captureNetworkError } from '../../config/sentry.config';
import { authTokenService } from '../../services/auth.service';
import { postGoogleAuth, GoogleSignupProfile } from '../../services/googleAuth.api';
import { navigateAfterGoogleAuth } from '../../utils/googleAuthNavigation';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import { API_BASE_URL } from '../../config/api';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
`;

const VersionBadge = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  font-size: 0.75rem;
  color: ${props => props.theme.mutedForeground};
  font-weight: 400;
  
  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: ${props => props.theme.foreground};
`;

const LastUpdated = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.mutedForeground};
  margin: 0;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1rem;
  color: ${props => props.theme.primary};
  text-decoration: none;
  font-size: 0.875rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AgreementSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.border};
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const AgreementCheckbox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  
  label {
    font-size: 0.875rem;
    color: ${props => props.theme.foreground};
    line-height: 1.5;
    cursor: pointer;
    
    a {
      color: ${props => props.theme.primary};
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const Content = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.foreground};
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 2rem 0 1rem 0;
    color: ${props => props.theme.foreground};
  }
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 1.5rem 0 0.75rem 0;
    color: ${props => props.theme.foreground};
  }
  
  p {
    margin: 1rem 0;
    color: ${props => props.theme.foreground};
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
    color: ${props => props.theme.foreground};
  }
  
  li {
    margin: 0.5rem 0;
  }
`;

const AcceptedMessage = styled.div`
  margin-top: 3rem;
  padding: 1rem;
  background: ${props => props.theme.muted};
  border: 1px solid ${props => props.theme.border};
  border-radius: ${props => props.theme.radius};
  color: ${props => props.theme.foreground};
  font-size: 0.875rem;
  text-align: center;
`;

const PrivacyPolicy: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isSignup = searchParams.get('signup') === 'true';
  const fromLogin = searchParams.get('from') === 'login';
  const [agreed, setAgreed] = useState(false);
  const [completingSignup, setCompletingSignup] = useState(false);
  const [googleCredential, setGoogleCredential] = useState<string | null>(null);
  const [googleSignupProfile, setGoogleSignupProfile] = useState<GoogleSignupProfile | null>(null);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean | null>(null);
  const [checkingTerms, setCheckingTerms] = useState(false);
  const navigate = useNavigate();
  const { signUpWithEmail, isAuthenticated, getAuthToken } = useAuth();
  const toast = useToast();

  useEffect(() => {
    if (!isSignup) {
      sessionStorage.removeItem('pendingSignup');
      return;
    }
    const raw = sessionStorage.getItem('pendingSignup');
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      if (data.isGoogle) {
        setGoogleSignupProfile({
          firstName: typeof data.firstName === 'string' ? data.firstName.trim() : undefined,
          lastName: typeof data.lastName === 'string' ? data.lastName.trim() : undefined,
          phone: typeof data.phone === 'string' ? data.phone.trim() : undefined,
          accountType: data.accountType === 'MANAGEMENT_FIRM' ? 'MANAGEMENT_FIRM' : 'LANDLORD',
          country: typeof data.country === 'string' ? data.country : 'KE',
        });
      }
    } catch {
      /* ignore */
    }
  }, [isSignup]);

  useEffect(() => {
    // Check if user has already accepted terms (only if authenticated and not in signup flow)
    const checkTermsStatus = async () => {
      if (isAuthenticated && !isSignup) {
        setCheckingTerms(true);
        try {
          const token = await getAuthToken();
          if (!token) {
            setCheckingTerms(false);
            return;
          }

          // Use API_BASE_URL which already includes /api
          const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
          const response = await fetch(`${apiUrl}/auth/terms-status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            setHasAcceptedTerms(data.hasAccepted);
          }
        } catch (error) {
          console.error('Error checking terms status:', error);
        } finally {
          setCheckingTerms(false);
        }
      }
    };

    checkTermsStatus();
  }, [isAuthenticated, isSignup, getAuthToken]);

  const handleCompleteSignup = async () => {
    if (!agreed) {
      toast.error('Agreement Required', {
        description: 'Please accept the Terms of Use and Privacy Policy to continue.',
      });
      return;
    }

    const pendingSignup = sessionStorage.getItem('pendingSignup');
    if (!pendingSignup) {
      // New flow: account is already created + phone verified before terms/privacy.
      if (isAuthenticated) {
        try {
          setCompletingSignup(true);
          const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
          const token = await getAuthToken();
          if (!token) throw new Error('Authentication expired. Please sign in again.');
          await fetch(`${apiUrl}/auth/accept-terms`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ version: '1.0' })
          });
          toast.success('Account setup complete', {
            description: 'Welcome! Redirecting to your dashboard...',
          });
          navigate('/signup-pending-approval');
          return;
        } catch (error: any) {
          toast.error('Could not complete setup', {
            description: error?.message || 'Please sign in and try again.',
          });
          return;
        } finally {
          setCompletingSignup(false);
        }
      }
      toast.error('Signup Data Missing', {
        description: 'Please return to the signup page and try again.',
      });
      navigate('/signup');
      return;
    }

    try {
      setCompletingSignup(true);
      const signupData = JSON.parse(pendingSignup);
      const signupProfile: GoogleSignupProfile = {
        firstName: typeof signupData.firstName === 'string' ? signupData.firstName.trim() : undefined,
        lastName: typeof signupData.lastName === 'string' ? signupData.lastName.trim() : undefined,
        phone: typeof signupData.phone === 'string' ? signupData.phone.trim() : undefined,
        accountType: signupData.accountType === 'MANAGEMENT_FIRM' ? 'MANAGEMENT_FIRM' : 'LANDLORD',
        country: typeof signupData.country === 'string' ? signupData.country : 'KE',
      };
      
      if (signupData.isGoogle) {
        if (!googleCredential) {
          throw new Error('Please sign in with Google using the button above before completing sign up.');
        }
        if (!signupProfile.phone) {
          sessionStorage.removeItem('pendingSignup');
          toast.error('Phone required', { description: 'Please sign up with your phone number on the registration page.' });
          navigate('/signup');
          return;
        }

        const result = await postGoogleAuth({
          credential: googleCredential,
          mode: 'signup',
          intent: 'landlord',
          profile: signupProfile,
        });

        authTokenService.setToken(result.token);
        const token = authTokenService.getToken();
        if (token) {
          await fetch(`${API_BASE_URL}/auth/accept-terms`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ version: '1.0' }),
          });
        }

        sessionStorage.removeItem('pendingSignup');
        navigateAfterGoogleAuth(result, navigate, { fromSignup: true });
        return;
      }

      {
        // Email/password signup
        const { email, password, firstName, lastName, accountType, country, phone } = signupData;
        const displayName = [firstName, lastName].filter(Boolean).join(' ').trim();
        
        // Complete the signup
        await signUpWithEmail(email, password, {
          phone: typeof phone === 'string' ? phone : undefined,
          accountType: accountType === 'MANAGEMENT_FIRM' ? 'MANAGEMENT_FIRM' : 'LANDLORD',
          country: typeof country === 'string' ? country : 'KE',
          displayName: displayName || undefined,
        });
        
        const token = authTokenService.getToken();
        if (token) {
          await fetch(`${API_BASE_URL}/auth/accept-terms`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ version: '1.0' }),
          });
        }
        
        // Clear pending signup data
        sessionStorage.removeItem('pendingSignup');
        
        toast.success('Account created successfully', {
          description: 'Welcome! Redirecting to your dashboard...',
        });
        navigate('/signup-pending-approval');
      }
    } catch (error: any) {
      console.error('❌ Sign-up completion failed:', error);
      
      // Capture error to Sentry
      captureError(error instanceof Error ? error : new Error(error.message || 'Signup failed'), {
        context: 'signup_completion',
        errorType: error.code || 'unknown',
      });
      
      const errorMessage = error.message || 'Failed to complete signup. Please try again.';
      toast.error('Signup failed', {
        description: errorMessage,
      });
    } finally {
      setCompletingSignup(false);
    }
  };

  return (
    <Container>
      <VersionBadge>v1.0</VersionBadge>
      
      {isSignup ? (
        <BackLink to="/terms?signup=true">← Back to Terms</BackLink>
      ) : (
        <BackLink to={fromLogin ? "/login" : "/signup"}>
          ← {fromLogin ? "Back to Sign In" : "Back to Sign Up"}
        </BackLink>
      )}
      
      <Header>
        <Title>Privacy Policy</Title>
        <LastUpdated>Last Updated: 05/01/2026</LastUpdated>
      </Header>

      <Content>
        <p>
          This Privacy Policy explains how TenantPlus, a property management and tenant services platform operated by Circle Plus Technologies ("Circle Plus", "we", "us", "our"), collects, uses, stores, and protects personal information when you use our platform.
        </p>
        <p>
          By accessing or using TenantPlus, you agree to the collection and use of information in accordance with this Privacy Policy.
        </p>

        <h2>1. Who This Policy Applies To</h2>
        <p>
          This policy applies to:
        </p>
        <ul>
          <li>Property Owners / Managers ("Landlords")</li>
          <li>Tenants ("Tenants")</li>
          <li>Visitors to our website or platform</li>
        </ul>
        <p>
          Each user type may provide different types of information depending on how they use the platform.
        </p>

        <h2>2. Information We Collect</h2>
        <p><strong>a) Information You Provide Directly</strong></p>
        <p>
          We may collect:
        </p>
        <ul>
          <li>Name, email address, phone number</li>
          <li>Account login details</li>
          <li>Property and tenancy information</li>
          <li>Payment-related information (not full card or bank details)</li>
          <li>Messages, requests, or communications sent through the platform</li>
          <li>Preferences and settings</li>
        </ul>
        <p><strong>b) Information Collected Automatically</strong></p>
        <p>
          When you use the platform, we may collect:
        </p>
        <ul>
          <li>IP address</li>
          <li>Device type and browser</li>
          <li>Log data and timestamps</li>
          <li>Usage activity (pages visited, features used)</li>
        </ul>

        <h2>3. Tenant and Landlord Data Distinction</h2>
        <ul>
          <li>Landlords control property and tenancy data they upload.</li>
          <li>Tenants control their personal profile and interaction data.</li>
          <li>Tenant data shown in the platform is often provided by landlords, and we do not independently verify its accuracy.</li>
        </ul>
        <p>
          We act as a data processor for certain landlord-provided data and as a service provider for tenant-facing services.
        </p>

        <h2>4. How We Use Information</h2>
        <p>
          We use collected information to:
        </p>
        <ul>
          <li>Provide and operate the TenantPlus platform</li>
          <li>Enable communication between landlords and tenants</li>
          <li>Deliver tenant-specific services</li>
          <li>Send service-related messages and notifications</li>
          <li>Process payments and subscriptions</li>
          <li>Improve platform performance and security</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p>
          We do not sell personal data.
        </p>

        <h2>5. Messaging & Communications</h2>
        <p>
          By using TenantPlus, you consent to receive service-related communications via:
        </p>
        <ul>
          <li>Email</li>
          <li>SMS</li>
          <li>WhatsApp or similar messaging platforms</li>
        </ul>
        <p>
          These messages may include:
        </p>
        <ul>
          <li>Payment reminders</li>
          <li>Notifications</li>
          <li>Account updates</li>
          <li>Service announcements</li>
        </ul>
        <p>
          You may manage communication preferences where available, but some service messages are essential and cannot be disabled.
        </p>

        <h2>6. Payments & Financial Data</h2>
        <ul>
          <li>We do not store full card or bank account details.</li>
          <li>Payments are processed through third-party payment providers.</li>
          <li>We may store transaction references, amounts, and status for record-keeping.</li>
        </ul>
        <p>
          Payment providers have their own privacy policies governing financial data.
        </p>

        <h2>7. Sharing of Information</h2>
        <p>
          We may share information:
        </p>
        <ul>
          <li>With third-party service providers (SMS, WhatsApp, hosting, payments)</li>
          <li>To comply with legal obligations or lawful requests</li>
          <li>To protect the rights, safety, or integrity of the platform</li>
        </ul>
        <p>
          We do not share data for advertising or resale.
        </p>

        <h2>8. Third-Party Services</h2>
        <p>
          TenantPlus integrates with third-party platforms such as:
        </p>
        <ul>
          <li>Messaging providers (e.g. SMS, WhatsApp)</li>
          <li>Payment processors</li>
          <li>Analytics and hosting services</li>
        </ul>
        <p>
          We are not responsible for the privacy practices of third parties. Users should review their respective privacy policies.
        </p>

        <h2>9. Data Retention</h2>
        <p>
          We retain personal data only for as long as necessary to:
        </p>
        <ul>
          <li>Provide services</li>
          <li>Meet legal and accounting requirements</li>
          <li>Resolve disputes</li>
        </ul>
        <p>
          Data may be retained after account closure where legally required.
        </p>

        <h2>10. Data Security</h2>
        <p>
          We take reasonable technical and organizational measures to protect data, including:
        </p>
        <ul>
          <li>Access controls</li>
          <li>Secure servers</li>
          <li>Encryption where appropriate</li>
        </ul>
        <p>
          However, no system is completely secure, and we cannot guarantee absolute security.
        </p>

        <h2>11. Your Rights</h2>
        <p>
          Depending on applicable law, users may have the right to:
        </p>
        <ul>
          <li>Access their personal data</li>
          <li>Request corrections</li>
          <li>Request deletion (subject to legal requirements)</li>
          <li>Withdraw consent for optional services</li>
        </ul>
        <p>
          Requests can be made by contacting us using the details below.
        </p>

        <h2>12. Children's Privacy</h2>
        <p>
          TenantPlus is not intended for use by individuals under the age of 18. We do not knowingly collect personal data from minors.
        </p>

        <h2>13. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Updates will be posted on the platform, and continued use constitutes acceptance of the revised policy.
        </p>

        <h2>14. Governing Law</h2>
        <p>
          This Privacy Policy is governed by the laws of the Republic of Kenya and is issued in accordance with the Kenya Data Protection Act, 2019, together with any applicable regulations made under the Act.
        </p>

        <h2>15. Contact Us</h2>
        <p>
          If you have questions or requests regarding this Privacy Policy, you may contact us at:
        </p>
        <p>
          <strong>Circle Plus Technologies</strong><br />
          Email: support@tenantplus.co.ke<br />
          Platform: TenantPlus
        </p>
      </Content>

      {isSignup && googleSignupProfile && (
        <AgreementSection>
          <p style={{ fontSize: '0.875rem', margin: 0 }}>
            Sign in with Google to link your account, then accept below.
          </p>
          <GoogleSignInButton
            onCredential={(credential) => {
              setGoogleCredential(credential);
              toast.success('Google account linked', { description: 'You can now complete sign up.' });
            }}
            onError={(msg) => toast.error('Google sign-in failed', { description: msg })}
            disabled={completingSignup}
            text="continue_with"
          />
        </AgreementSection>
      )}

      {isSignup && (
        <AgreementSection>
          <AgreementCheckbox>
            <Checkbox
              checked={agreed}
              onChange={(checked) => setAgreed(checked)}
              id="terms-privacy-agreement"
            />
            <label htmlFor="terms-privacy-agreement">
              I agree to the <Link to={`/terms?signup=true${fromLogin ? '&from=login' : ''}`}>Terms of Use</Link> and{' '}
              <Link to={`/privacy?signup=true${fromLogin ? '&from=login' : ''}`}>Privacy Policy</Link>
            </label>
          </AgreementCheckbox>
          
          <Button
            variant="primary"
            size="lg"
            onClick={handleCompleteSignup}
            disabled={!agreed || completingSignup}
            style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}
          >
            {completingSignup ? 'Creating Account...' : 'I Agree - Complete Sign Up'}
          </Button>
        </AgreementSection>
      )}

      {!isSignup && isAuthenticated && hasAcceptedTerms === true && (
        <AcceptedMessage>
          ✓ You have already accepted the Terms of Use and Privacy Policy.
        </AcceptedMessage>
      )}
    </Container>
  );
};

export default PrivacyPolicy;


